import {
  ChevronLeftIcon,
  ChevronRightIcon,
  DoubleArrowLeftIcon,
  DoubleArrowRightIcon,
} from '@radix-ui/react-icons';
import type { Table } from '@tanstack/react-table';

import { Button } from '../button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../select';

interface DataTablePaginationProps<TData> {
  disabled?: boolean;
  id?: string;
  pageNumberOfOptions?: number;
  pageSize?: number;
  table: Table<TData>;
}

export function DataTablePagination<TData>({
  disabled = false,
  id,
  pageNumberOfOptions = 5,
  pageSize = 10,
  table,
}: DataTablePaginationProps<TData>) {
  const pageSteps = Array.from(
    Array(pageNumberOfOptions + 1).keys(),
    (val) => val * pageSize,
  ).filter((val) => val !== 0);

  return (
    <div className="flex flex-wrap items-center justify-between gap-4">
      <div className="text-sm text-muted-foreground">
        {table.getCoreRowModel().rows.length} registro
        {table.getCoreRowModel().rows.length === 1 ? '' : 's'}
      </div>
      <div className="flex flex-wrap justify-end gap-2">
        <div className="flex items-center space-x-2">
          <p className="font-regular text-sm">Registros por página</p>
          <Select
            disabled={disabled}
            onValueChange={(value) => {
              table.setPageSize(Number(value));
            }}
            value={`${table.getState().pagination.pageSize}`}
          >
            <SelectTrigger id={id ? `${id}-pagination-select` : undefined} className="h-8 w-16">
              <SelectValue placeholder={table.getState().pagination.pageSize} />
            </SelectTrigger>
            <SelectContent side="top">
              {pageSteps.map((pageSize) => (
                <SelectItem
                  id={id ? `${id}-pagination-select-${pageSize}` : undefined}
                  key={pageSize}
                  value={`${pageSize}`}
                >
                  {pageSize}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="font-regular flex w-28 items-center justify-center text-sm">
          Página {table.getState().pagination.pageIndex + 1} de {table.getPageCount()}
        </div>
        <div className="flex items-center space-x-2">
          <Button
            id={id ? `${id}-pagination-navigateToFirst` : undefined}
            className="size-8 p-0"
            disabled={!table.getCanPreviousPage() || disabled}
            onClick={() => {
              table.setPageIndex(0);
            }}
            variant="outline"
          >
            <span className="sr-only">Ir a la primera página</span>
            <DoubleArrowLeftIcon className="size-4" />
          </Button>
          <Button
            id={id ? `${id}-pagination-navigateToPrevious` : undefined}
            className="size-8 p-0"
            disabled={!table.getCanPreviousPage() || disabled}
            onClick={() => {
              table.previousPage();
            }}
            variant="outline"
          >
            <span className="sr-only">Ir a la página anterior</span>
            <ChevronLeftIcon className="size-4" />
          </Button>
          <Button
            id={id ? `${id}-pagination-navigateToNext` : undefined}
            className="size-8 p-0"
            disabled={!table.getCanNextPage() || disabled}
            onClick={() => {
              table.nextPage();
            }}
            variant="outline"
          >
            <span className="sr-only">Ir a la siguiente página</span>
            <ChevronRightIcon className="size-4" />
          </Button>
          <Button
            id={id ? `${id}-pagination-navigateToLast` : undefined}
            className="size-8 p-0"
            disabled={!table.getCanNextPage() || disabled}
            onClick={() => {
              table.setPageIndex(table.getPageCount() - 1);
            }}
            variant="outline"
          >
            <span className="sr-only">Ir a la última página</span>
            <DoubleArrowRightIcon className="size-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
